"use client";

import { useCallback, useMemo } from "react";
import { useTableRowActionContext } from "@/lib/hooks/ui/useTableRowActionContext";
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import { executeDetailAction } from "@/components/shared/UniversalDetailPage/actions/execute-action";
import {
  getActionCategory,
  partitionByCategory,
  sortActionsByCategory,
  type ActionCategory,
} from "@/lib/actions/action-categories";
import type {
  DetailAction,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type {
  EntityHooks,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";

export interface RowActionItem<T extends BaseEntity> {
  action: DetailAction<T>;
  label: string;
  isDisabled: boolean;
  isDestructive: boolean;
  /** Priority bucket — consumers insert a divider when this changes. */
  category: ActionCategory;
  run: () => void;
}

// The pure grouping helper lives in its own import-light module so it can be
// unit-tested and shared without dragging in the action-execution graph.
export { groupRowActionItems, type RowActionItemGroup } from "./row-action-groups";

interface UseRowActionItemsParams<T extends BaseEntity> {
  item: T;
  actions: DetailAction<T>[];
  hooks: EntityHooks<T>;
  entityName: string;
  config?: SimpleTableConfig<T>;
}

/**
 * Shared source of truth for an entity row's actions — the exact same
 * role-filtering + execution path used by the desktop right-click context menu
 * and dropdown. Consumed by both `RowActionsDropdownMenu` (desktop dropdown) and
 * `MobileRowActionsSheet` (mobile bottom sheet) so the two never diverge.
 */
export function useRowActionItems<T extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
}: UseRowActionItemsParams<T>): {
  ready: boolean;
  items: RowActionItem<T>[];
  partition: { top: RowActionItem<T>[]; more: RowActionItem<T>[]; danger: RowActionItem<T>[] };
} {
  const updateMutation = hooks.useUpdate();
  const deleteMutation = hooks.useDelete();
  const refetch = useCallback(() => Promise.resolve(), []);
  const { filterActionsByRole } = useActionFiltering();

  const rowActionContext = useTableRowActionContext({
    item,
    hooks,
    refetch,
    updateMutation,
    deleteMutation,
    entityName,
    config,
  });

  const visibleActions = useMemo(() => {
    const roleFilteredActions = filterActionsByRole(actions);
    return roleFilteredActions.filter((action) => {
      if (action.visible && typeof action.visible === "function") {
        return action.visible(item);
      }
      return true;
    });
  }, [actions, item, filterActionsByRole]);

  const items = useMemo<RowActionItem<T>[]>(() => {
    if (!rowActionContext) return [];
    // Priority order so the most-likely actions surface first, destructive last.
    return sortActionsByCategory(visibleActions).map((action) => {
      const label = typeof action.label === "function" ? action.label(item) : action.label;
      const isDisabled = Boolean(
        action.disabled && typeof action.disabled === "function" && action.disabled(item)
      );
      return {
        action,
        label,
        isDisabled,
        isDestructive: action.variant === "destructive",
        category: getActionCategory(action),
        run: () => {
          if (isDisabled || !rowActionContext) return;
          void executeDetailAction(action, rowActionContext);
        },
      };
    });
  }, [visibleActions, item, rowActionContext]);

  // Collapse zones: `top` (inline), `more` (behind a "More…" disclosure),
  // `danger` (pinned last). Consumers render the three zones; the flat `items`
  // list is kept for callers that don't collapse.
  const partition = useMemo(() => partitionByCategory(items), [items]);

  return {
    ready: Boolean(rowActionContext) && visibleActions.length > 0,
    items,
    partition,
  };
}
